import { withStyles, CircleButton, Box, Card, GetColorStatus, parseJWT, TextField } from 'vopay';
import type { ApiCredentials, EncodedCredentials, SupportedCurrencies } from 'vopay';

import { FunctionComponent, useContext, useState } from 'react';
import { Prompt, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AccountTabs from './AccountTabs';
import AccountPermissionsDrawer from './AccountPermissionsDrawer/AccountPermissionsDrawer';
import AccountContext from '../AccountContext';

import { usePendingData } from '@/providers/PendingData.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useDialog, type OpenDialogProps } from '@/providers/Dialog.provider';
import { Title, UtilButton, TransactionStatus } from '@/components/UI';
import CopyButton from '@/components/UI/CopyButton/CopyButton';
import SendEmailDialog, { type SendEmailDialogResult } from './SendEmailDIalog';

import ActiveIcon from './svg/Active.svg';
import PendingIcon from './svg/Pending.svg';

import styles from './Account.module.scss';
import type { AccountsHandlers, Balances as Balance } from '../types';

const cn = withStyles(styles);

type AccountProps = {
  hasValidOnboardingUrl: boolean | null;
  handlers: AccountsHandlers;

  onBoardingLink: string | null;
};

type FC = FunctionComponent<AccountProps>;

const Account: FC = ({ hasValidOnboardingUrl, onBoardingLink, handlers }: AccountProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account' });
  const { t: tStatus } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });
  const { details, transactions } = useContext(AccountContext);
  const { hasPendingData } = usePendingData();

  const { canAccessOnboardingTokenPanel, canSeeAccountCredential, canUpdatePermissions, canSendOnboardingEmail } =
    useModuleFeatureFlag('accounts');

  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);

  const { accountId } = useParams<{ accountId: string }>();

  const dialog = useDialog();

  const handleCopyClick = () => {
    if (onBoardingLink) void navigator?.clipboard?.writeText(onBoardingLink);
  };

  if (!details[accountId]) {
    return null;
  }

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const query = new URLSearchParams(useLocation().search);

  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  const isPending = canAccessOnboardingTokenPanel && balance.accountStatus === 'Application Pending';
  const credentials = canSeeAccountCredential && balance.isActive ? getCredentials(details[accountId].token) : null;

  const handleSendEmail = async () => {
    const BoundDialog = ({ onConfirm, onCancel }: OpenDialogProps<SendEmailDialogResult>) => (
      <SendEmailDialog
        onConfirm={onConfirm}
        onCancel={onCancel}
        email={details[accountId].contact.email}
        entityType="Account"
        entityID={accountId}
      />
    );

    // Success needs no follow-up here (as before); a failure is toasted by the dialog itself.
    await dialog.open(BoundDialog);
  };

  /**
   * `parseJWT` throws on anything it can't split into JWT segments and base64-decode, so a corrupt or
   * truncated account token must not be dereferenced blind — it used to take the whole account page
   * down with it. A token that won't decode means no credentials to show, not a blank page.
   */
  function getCredentials(token: string): EncodedCredentials | ApiCredentials | null {
    try {
      const json = parseJWT(token);
      const defaults: EncodedCredentials = { AccountID: '', ApiKey: '', ApiSharedSecret: '' };

      return isValidCredentials(json) ? json : defaults;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  function isValidCredentials(json: unknown): json is EncodedCredentials | ApiCredentials {
    return (
      Object.prototype.hasOwnProperty.call(json, 'AccountID') && Object.prototype.hasOwnProperty.call(json, 'ApiKey')
    );
  }

  function isEncodedCredentials(creds: EncodedCredentials | ApiCredentials): creds is EncodedCredentials {
    return Object.prototype.hasOwnProperty.call(creds, 'ApiSharedSecret');
  }

  function getSecretLabel(creds: EncodedCredentials | ApiCredentials): string {
    return isEncodedCredentials(creds) ? t('Credentials.apiSharedSecret') : t('Credentials.apiSignature');
  }

  function getSecretValue(creds: EncodedCredentials | ApiCredentials): string {
    return isEncodedCredentials(creds) ? creds.ApiSharedSecret : (creds as ApiCredentials).ApiSignature;
  }

  return (
    <Box gapSize="24px" flx="col" maxwidth="1120px" className={cn('accountContainer')}>
      <UtilButton
        size="medium"
        iconLeft="chevron_left"
        onClick={() => {
          history.back();
        }}
      >
        {t('backButton')}
      </UtilButton>

      {Object.values(details).length && (
        <>
          <Card style={{ padding: '0px' }}>
            <div className={cn('header', credentials && 'headerWithCredentials')}>
              <div className={cn('headerRight')}>
                <img
                  src={balance.isActive ? ActiveIcon : PendingIcon}
                  alt={t(balance.isActive ? 'iconAltActive' : 'iconAltPending', {
                    defaultValue: balance.isActive ? 'Active Account' : 'Pending Account',
                  })}
                  className={cn('accountIcon')}
                />

                <div className={cn('titleGroupContainer')}>
                  <div className={cn('titleGroup')}>
                    <Title level="3">{details[accountId].accountName}</Title>
                    <span className={cn('currencySepearator')}>|</span>{' '}
                    <span className={cn('currency')}>{balance.currency}</span>
                  </div>

                  {canUpdatePermissions && (
                    <UtilButton
                      className={cn('viewPermissionsButton')}
                      size="medium"
                      onClick={() => setIsPermissionsOpen(true)}
                      iconRightProps={{
                        fillAmount: 1,
                        icon: 'edit',
                      }}
                    >
                      {t('viewPermissions')}
                    </UtilButton>
                  )}
                </div>
              </div>

              <div className={cn('headerLeft')}>
                <div className={cn('statusContainer')}>
                  {balance.isActive ? (
                    <TransactionStatus label={tStatus('Active')} status="positive" />
                  ) : (
                    !!balance.accountStatus && (
                      <TransactionStatus
                        label={tStatus(balance.accountStatus)}
                        status={GetColorStatus(balance.accountStatus)}
                      />
                    )
                  )}
                </div>

                {isPending && (
                  <div className={cn('onboardingUrl')}>
                    <TextField
                      inputClass={cn('readonlyInput')}
                      name="onboardinUrl"
                      readOnly
                      variant="outlineShadow"
                      value={onBoardingLink || ''}
                    />

                    <div className={cn('onboardingActions')}>
                      <CircleButton
                        colorScheme="action"
                        icon="content_copy"
                        onClick={handleCopyClick}
                        disabled={!hasValidOnboardingUrl || isRoleViewOnly}
                      />

                      {canSendOnboardingEmail && (
                        <CircleButton
                          colorScheme="action"
                          icon="send"
                          data-testid="accountHeader-sendOnboardingEmail"
                          onClick={handleSendEmail}
                          disabled={!hasValidOnboardingUrl || isRoleViewOnly}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {credentials && (
              <div className={cn('credentials')}>
                <div className={cn('credentialItem')}>
                  <span className={cn('credentialLabel')}>{t('Credentials.accountId')}</span>

                  <div className={cn('credentialValue')}>
                    <span>{credentials.AccountID}</span>
                    <CopyButton content={credentials.AccountID} />
                  </div>
                </div>

                <div className={cn('credentialItem')}>
                  <span className={cn('credentialLabel')}>{t('Credentials.apiKey')}</span>

                  <div className={cn('credentialValue')}>
                    <span>{credentials.ApiKey}</span>
                    <CopyButton content={credentials.ApiKey} />
                  </div>
                </div>

                <div className={cn('credentialItem')}>
                  <span className={cn('credentialLabel')}>{getSecretLabel(credentials)}</span>

                  <div className={cn('credentialValue')}>
                    <span>{getSecretValue(credentials)}</span>
                    <CopyButton content={getSecretValue(credentials)} />
                  </div>
                </div>
              </div>
            )}
          </Card>

          <AccountPermissionsDrawer
            isOpen={isPermissionsOpen}
            onClose={() => setIsPermissionsOpen(false)}
            accountName={details[accountId].accountName}
          />

          <AccountTabs
            transactions={transactions}
            handlers={handlers}
            isPending={isPending}
            onBoardingLink={onBoardingLink}
            hasValidOnboardingUrl={hasValidOnboardingUrl}
            onCopy={handleCopyClick}
            onSendEmail={handleSendEmail}
          />

          {/* TODO: dead Prompt, remove — hasPendingData is never set here since permissions moved into AccountPermissionsDrawer (AccountsContainer's initializePermissions/onPermissionChange have no callers). */}
          <Prompt when={hasPendingData} message={t('unsavedChangesPrompt')} />
        </>
      )}
    </Box>
  );
};

export default Account;
